import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import type { FilterHierarchyConfig } from '~/shared/utils/filterHierarchy';
import {
  applyCascadingLevelChange,
  buildSelectionFromPartial,
  getCascadingOptionsAtLevel,
  selectionToFilterParams,
} from '~/shared/utils/cascadingNarrativeFilters';
import { useSearchParams } from '~/shared/hooks';
import DropdownFilter from '../DropDownFilter/DropDownFilter';

/** Flatten into FilterBarContainer so these dropdowns align with peer filters. */
const Row = styled.div`
  display: contents;
`;

type CascadingNarrativeFilterBarProps = {
  tree: FilterHierarchyConfig;
  values?: Record<string, string | string[] | undefined>;
  tooltipCfg?: any;
};

export default function CascadingNarrativeFilterBar({ tree, values, tooltipCfg }: CascadingNarrativeFilterBarProps) {
  const { setParams } = useSearchParams();
  const hierarchy = tree?.filterHierarchy ?? (tree as any)?.hierarchy ?? [];

  const urlPartial = useMemo(() => {
    const partial: Record<string, string | undefined> = {};
    hierarchy.forEach((level: { id: string; name: string }) => {
      const raw = values?.[level.id] ?? values?.[level.name];
      if (raw === undefined || raw === null) return;
      partial[level.id] = Array.isArray(raw) ? String(raw[0]) : String(raw);
    });
    return partial;
  }, [hierarchy, values]);

  const [selection, setSelection] = useState<Record<string, string>>(() =>
    hierarchy.length ? buildSelectionFromPartial(tree, urlPartial) : {},
  );

  const skipNextSyncRef = useRef(false);

  useEffect(() => {
    if (!hierarchy.length) return;
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }
    const next = buildSelectionFromPartial(tree, urlPartial);
    setSelection(next);

    // Table/filter clicks may set only a leaf (e.g. company). Push resolved ancestors too.
    const { ids } = selectionToFilterParams(tree, next);
    const missingFromUrl = Object.entries(ids).some(([key, value]) => {
      const raw = values?.[key];
      return Boolean(value) && (raw === undefined || raw === null || raw === '');
    });
    if (missingFromUrl) {
      skipNextSyncRef.current = true;
      setParams({ newParams: ids });
    }
  }, [tree, urlPartial, hierarchy.length, setParams, values]);

  const pushSelection = useCallback(
    (next: Record<string, string>) => {
      skipNextSyncRef.current = true;
      setSelection(next);
      const { ids } = selectionToFilterParams(tree, next);
      setParams({ newParams: ids });
    },
    [setParams, tree],
  );

  // Ensure URL gets first-valid defaults on mount when params are missing.
  useEffect(() => {
    if (!hierarchy.length) return;
    const { ids } = selectionToFilterParams(tree, selection);
    const missing = Object.entries(ids).some(([key, value]) => {
      const raw = values?.[key];
      return value && (raw === undefined || raw === null || raw === '');
    });
    if (missing) {
      pushSelection(selection);
    }
    // Only on mount / when tree identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree]);

  const handleLevelChange = useCallback(
    (levelId: string, nextId: string) => {
      const next = applyCascadingLevelChange(tree, selection, levelId, nextId);
      pushSelection(next);
    },
    [pushSelection, selection, tree],
  );

  if (!hierarchy.length) {
    return null;
  }

  return (
    <Row>
      {hierarchy.map((level: { id: string; name: string }, levelIndex: number) => {
        const options = getCascadingOptionsAtLevel(tree, selection, levelIndex);
        const selectedId = selection[level.id] ?? '';
        // Do not inject missing selected ids into options — that lets invalid
        // company/company-type pairs appear selected and causes widget-data 500s.
        return (
          <DropdownFilter
            key={level.id}
            name={level.name}
            value={selectedId}
            options={options}
            tooltipCfg={tooltipCfg}
            onChange={(nextValue: string | string[]) => {
              const resolved = Array.isArray(nextValue) ? nextValue[0] : nextValue;
              if (!resolved) return;
              handleLevelChange(level.id, resolved);
            }}
          />
        );
      })}
    </Row>
  );
}
